import React from 'react';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { library, IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCheckCircle,
  faVolumeOff,
  faVolumeDown,
  faVolume,
  faVolumeUp,
  faPaperPlane,
  faClock,
  faCaretUp,
  faCaretDown,
  faUsers,
  faImage,
  faTrash,
  faTimesCircle,
  faPhone,
  faExpand,
  faCompress,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faTimes,
  faChevronRight,
  faChevronLeft,
  faChevronDown,
  faLevelDown,
  faLevelUp,
  faCheck,
  faCopy,
  faCalendar,
  faBars,
  faFontCase,
  faCropAlt,
  faSync,
  faEllipsisV,
  faPlus,
  faMinus,
  faChevronUp,
  faPlayCircle,
  faQuestionCircle,
  faEye,
  faEyeSlash,
  faInfoCircle,
  faPen,
  faBoxOpen,
  faPlusCircle,
  faMapMarkerAlt,
  faDumbbell,
} from '@fortawesome/pro-regular-svg-icons';
import useThemeColor from 'components/material/use-theme-color';

export type IconName =
  | 'check-circle'
  | 'check'
  | 'times-circle'
  | 'times'
  | 'chevron-right'
  | 'chevron-left'
  | 'chevron-down'
  | 'chevron-up'
  | 'level-down'
  | 'level-up'
  | 'volume-off'
  | 'volume-down'
  | 'volume'
  | 'volume-up'
  | 'copy'
  | 'paper-plane'
  | 'clock'
  | 'calendar'
  | 'caret-up'
  | 'caret-down'
  | 'users'
  | 'bars'
  | 'image'
  | 'font-case'
  | 'trash'
  | 'crop-alt'
  | 'sync'
  | 'ellipsis-v'
  | 'plus'
  | 'minus'
  | 'play-circle'
  | 'phone'
  | 'expand'
  | 'compress'
  | 'microphone'
  | 'microphone-slash'
  | 'video'
  | 'question-circle'
  | 'eye'
  | 'eye-slash'
  | 'info-circle'
  | 'box-open'
  | 'pen'
  | 'plus-circle'
  | 'map-marker'
  | 'dumbbell';
interface Props extends Omit<FontAwesomeIconProps, 'color'> {
  icon: IconName;
  color?: string;
}

library.add(
  faTimes,
  faCheckCircle,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faCheck,
  faLevelDown,
  faLevelUp,
  faChevronLeft,
  faVolumeOff,
  faVolumeDown,
  faVolume,
  faVolumeUp,
  faCopy,
  faPaperPlane,
  faClock,
  faCalendar,
  faCaretDown,
  faCaretUp,
  faUsers,
  faBars,
  faImage,
  faFontCase,
  faTrash,
  faCropAlt,
  faSync,
  faEllipsisV,
  faTimesCircle,
  faPlus,
  faMinus,
  faPlayCircle,
  faPhone,
  faExpand,
  faCompress,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faPlayCircle,
  faQuestionCircle,
  faEye,
  faEyeSlash,
  faInfoCircle,
  faPen,
  faBoxOpen,
  faPlusCircle,
  faMapMarkerAlt,
  faDumbbell,
);

function toFAIcon(icon: IconName): IconProp {
  switch (icon) {
    case 'check':
      return ['far', 'check'];
    case 'times':
      return ['far', 'times'];
    case 'chevron-right':
      return ['far', 'chevron-right'];
    case 'chevron-left':
      return ['far', 'chevron-left'];
    case 'chevron-down':
      return ['far', 'chevron-down'];
    case 'chevron-up':
      return ['far', 'chevron-up'];
    case 'level-down':
      return ['far', 'level-down'];
    case 'level-up':
      return ['far', 'level-up'];
    case 'copy':
      return ['far', 'copy'];
    case 'calendar':
      return ['far', 'calendar'];
    case 'bars':
      return ['far', 'bars'];
    case 'font-case':
      return ['far', 'font-case'];
    case 'crop-alt':
      return ['far', 'crop-alt'];
    case 'sync':
      return ['far', 'sync'];
    case 'ellipsis-v':
      return ['far', 'ellipsis-v'];
    case 'plus':
      return ['far', 'plus'];
    case 'minus':
      return ['far', 'minus'];
    case 'play-circle':
      return ['far', 'play-circle'];
    case 'question-circle':
      return ['far', 'question-circle'];
    case 'eye':
      return ['far', 'eye'];
    case 'eye-slash':
      return ['far', 'eye-slash'];
    case 'info-circle':
      return ['far', 'info-circle'];
    case 'pen':
      return ['far', 'pen'];
    case 'box-open':
      return ['far', 'box-open'];
    case 'plus-circle':
      return ['far', 'plus-circle'];
    case 'map-marker':
      return ['far', 'map-marker-alt'];
    case 'dumbbell':
      return ['far', 'dumbbell'];
    default:
      return icon;
  }
}

export default function Icon({ icon, color, ...props }: Props): JSX.Element {
  const iconColor = useThemeColor(color || 'default.black');
  return <FontAwesomeIcon icon={toFAIcon(icon)} color={iconColor} {...props} />;
}
